import { defineAsyncComponent } from 'vue';
import { PRIMITIVE_TYPE_DATE, PRIMITIVE_TYPE_DATE_TIME, PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE, PRIMITIVE_TYPE_TIME, PRIMITIVE_TYPES_EPOCH_INTEGER } from '#layers/data-model/constants/schema/primitive-type';
import { defineFieldTypeTemplate } from '#layers/data-model/field-types/define';
import { FilterOperator } from '#layers/data-model/types/filters';
import { getDefaultTimezoneFormFields } from '#layers/data-model/utils/fields/datetime';

export interface DatetimeFieldOptions {
	defaultTimezone?: string;
}

const DateTimeFilterInput = defineAsyncComponent(() => import('./FilterInput.vue'));
const DateTimeFilterInputGroup = defineAsyncComponent(() => import('./FilterInputGroup.vue'));

const DATETIME_PRIMITIVE_TYPES = [
	PRIMITIVE_TYPE_DATE_TIME,
	PRIMITIVE_TYPE_DATE_TIME_WITH_TIMEZONE,
	PRIMITIVE_TYPE_DATE,
	PRIMITIVE_TYPE_TIME,
	...PRIMITIVE_TYPES_EPOCH_INTEGER,
] as const;

const FILTER_PRIMITIVE_TYPES = new Set<string>(DATETIME_PRIMITIVE_TYPES);
const TIME_BEARING_PRIMITIVE_TYPES = new Set<string>(DATETIME_PRIMITIVE_TYPES.filter((type) => type !== PRIMITIVE_TYPE_DATE));

const DATE_OPERATOR_LABELS: Partial<Record<FilterOperator, string>> = {
	[FilterOperator.LT]: 'field_types.datetime.filter_operators._lt',
	[FilterOperator.LTE]: 'field_types.datetime.filter_operators._lte_on',
	[FilterOperator.GT]: 'field_types.datetime.filter_operators._gt',
	[FilterOperator.GTE]: 'field_types.datetime.filter_operators._gte_on',
};

const MOMENT_OPERATOR_LABELS: Partial<Record<FilterOperator, string>> = {
	[FilterOperator.LT]: 'field_types.datetime.filter_operators._lt',
	[FilterOperator.LTE]: 'field_types.datetime.filter_operators._lte_at',
	[FilterOperator.GT]: 'field_types.datetime.filter_operators._gt',
	[FilterOperator.GTE]: 'field_types.datetime.filter_operators._gte_at',
};

const SUPPORTED_FILTER_OPERATORS = [
	FilterOperator.EQ,
	FilterOperator.NEQ,
	FilterOperator.LT,
	FilterOperator.LTE,
	FilterOperator.GT,
	FilterOperator.GTE,
	FilterOperator.BETWEEN,
	FilterOperator.NBETWEEN,
];

export default defineFieldTypeTemplate<DatetimeFieldOptions>({
	type: 'primitive',
	id: 'datetime',
	name: '$t:field_types.datetime.name',
	description: '$t:field_types.datetime.description',
	icon: 'lucide:calendar',
	sort: 500,
	keywords: ['date', 'time', 'timestamp', 'calendar'],
	supportedPrimitiveTypes: DATETIME_PRIMITIVE_TYPES,
	options: ({ form, context }) => form(getDefaultTimezoneFormFields(context.primitiveType)),
	filter: ({ primitiveType }) => {
		if (!FILTER_PRIMITIVE_TYPES.has(primitiveType)) return;

		const isTimeBearing = TIME_BEARING_PRIMITIVE_TYPES.has(primitiveType);

		return {
			input: DateTimeFilterInput,
			inputGroup: DateTimeFilterInputGroup,
			supportedOperators: SUPPORTED_FILTER_OPERATORS,
			defaultOperator: isTimeBearing ? FilterOperator.LT : undefined,
			operatorLabels: isTimeBearing ? MOMENT_OPERATOR_LABELS : DATE_OPERATOR_LABELS,
		};
	},
});
